import {
  BarChart3,
  Check,
  ChevronsLeft,
  ChevronsUpDown,
  CirclePlus,
  Clapperboard,
  ClipboardList,
  CreditCard,
  Disc,
  DollarSign,
  FileText,
  Home,
  LifeBuoy,
  Lock,
  Palette,
  Plus,
  Tag,
  UserCircle,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'

/* The artist portal, recreated from Studio V2 — and re-brandable.
 *
 * Every competitor writes some version of "fully branded platform" and then
 * shows a screenshot of THEIR product. That is the one claim a screenshot
 * structurally cannot make, because the screenshot has to be branded as
 * somebody. Letting the reader flip the brand is the argument, made in the
 * only medium that can make it.
 *
 * NO TYPE BELOW 10px, AND THAT IS A HARD RULE HERE. An earlier version scaled
 * the whole product down by using 8px labels, and on a browser with a minimum
 * font size configured — a common accessibility setting, and Chrome ships the
 * control — every one of those labels jumped to the floor while the rest of
 * the layout stayed put. The small caps ended up competing with the headings
 * and the mock looked broken, which is a bad way for a marketing page to fail.
 * So the type scale starts at 10px and the mock is sized around it, rather
 * than the other way round. It is also simply better practice: 8px is below
 * any reasonable legibility floor even when it renders as asked.
 *
 * WHOSE SCREEN THIS IS, and an earlier version had it backwards. This is the
 * ARTIST's dashboard inside a label's branded deployment — so the greeting says
 * the ARTIST's name and the numbers are one artist's catalogue, while the logo,
 * the domain and the sender belong to the label. Greeting the label at a screen
 * the label's artist is logged into also made the figures wrong: 128 releases
 * and 119 live is a catalogue, not a person.
 *
 * Getting it right strengthens the argument rather than weakening it. That
 * split — their name, your brand — is exactly what a white-label deployment
 * sells, and it is legible in one glance.
 *
 * REBUILT FROM STUDIO V2 (src/shell/navSections.ts, src/home/*, src/index.css).
 * The sidebar groups are exactly the real ones, minus the optional studios a
 * tenant may not have. The Home is music-first: a latest-release card with its
 * four-step journey, a balance card, and a "Your music" grid of cover tiles.
 * Every figure and title is a sample, and says so.
 *
 * Values read from index.css, not guessed: shell #050506, panel #0a0a0a,
 * border white@8%, one radius of 0.5rem, nav active white@7.5%, success
 * rgb(34,197,94), tint-blue rgb(96,165,250), display face Space Grotesk.
 *
 * WHERE THE BRAND COLOUR GOES, and it is narrow: the logo, the active nav
 * item's left bar and icon, and primary buttons. The studio's own source says
 * `--color-primary` is "reserved for CTA fills". The journey bars and
 * status colours (success green, progress blue) are fixed chrome and do not
 * follow the tenant as the brand switches.
 *
 * The tenants are invented and must stay invented — a real label's name on a
 * mock dashboard is an endorsement we do not have.
 */

interface Tenant {
  key: string
  name: string
  domain: string
  wordmark: string
  accent: string
  user: { name: string; email: string; initial: string }
}

const TENANTS: Tenant[] = [
  {
    key: 'northwave',
    name: 'Northwave',
    domain: 'artists.northwave.com',
    wordmark: 'NORTHWAVE',
    accent: '#7c3aed',
    user: { name: 'Mira Solace', email: 'mira@northwave.com', initial: 'M' },
  },
  {
    key: 'kalari',
    name: 'Kalari Sound',
    domain: 'studio.kalarisound.in',
    wordmark: 'KALARI',
    accent: '#20c5a8',
    user: { name: 'Ravi Menon', email: 'ravi@kalarisound.in', initial: 'R' },
  },
  {
    key: 'sable',
    name: 'Sable Row',
    domain: 'portal.sablerow.co',
    wordmark: 'SABLE ROW',
    accent: '#e8b23a',
    user: { name: 'Jo Adeyemi', email: 'jo@sablerow.co', initial: 'J' },
  },
  {
    key: 'yours',
    name: 'Your label',
    domain: 'artists.yourlabel.com',
    wordmark: 'YOUR LOGO',
    accent: '#ff6a1a',
    user: { name: 'Ana Reyes', email: 'ana@yourlabel.com', initial: 'A' },
  },
]

/* The groups of Studio V2's navSections.ts, in its order and with its icons.
   The optional Video Studio entry is not drawn: it is a per-tenant grant. */
const NAV: { group: string | null; items: { label: string; icon: LucideIcon }[] }[] = [
  { group: null, items: [{ label: 'Home', icon: Home }] },
  {
    group: 'Catalog',
    items: [
      { label: 'Catalog', icon: Disc },
      { label: 'New release', icon: CirclePlus },
      { label: 'Artwork', icon: Palette },
      { label: 'Labels', icon: Tag },
    ],
  },
  {
    group: 'Money',
    items: [
      { label: 'Earnings', icon: DollarSign },
      { label: 'Statements', icon: FileText },
      { label: 'Analytics', icon: BarChart3 },
      { label: 'Payouts', icon: Wallet },
      { label: 'Billing', icon: CreditCard },
    ],
  },
  {
    group: 'Requests',
    items: [
      { label: 'Forms', icon: ClipboardList },
      { label: 'VEVO', icon: Clapperboard },
      { label: 'Support', icon: LifeBuoy },
    ],
  },
  { group: 'Settings', items: [{ label: 'Account', icon: UserCircle }] },
]

const ACTIVE_NAV = 'Home'

/* The four journey steps of releaseJourney.ts (labels are the status badges'
   own). This sample release is in review: Draft done, In review current. */
const JOURNEY = [
  { label: 'Draft', state: 'done' },
  { label: 'In review', state: 'current' },
  { label: 'Delivering', state: 'todo' },
  { label: 'Live', state: 'todo' },
] as const

const JOURNEY_BAR = {
  done: 'rgb(34,197,94)',
  current: 'rgb(96,165,250)',
  todo: 'rgba(255,255,255,0.1)',
} as const

/* Invented titles; the dot colours are STATUS_META tones (success / blue / grey). */
const TILES = [
  { title: 'Night Bloom', status: 'In review', dot: 'rgb(96,165,250)', hue: 265 },
  { title: 'Paper Lanterns', status: 'Live', dot: 'rgb(34,197,94)', hue: 170 },
  { title: 'Slow Static', status: 'Live', dot: 'rgb(34,197,94)', hue: 35 },
  { title: 'Harbour Lights', status: 'Live', dot: 'rgb(34,197,94)', hue: 210 },
  { title: 'Cold Open', status: 'Draft', dot: 'rgba(226,232,240,0.75)', hue: 320 },
  { title: 'Small Hours', status: 'Live', dot: 'rgb(34,197,94)', hue: 100 },
]

const PANEL = '#0a0a0a'
const SHELL = '#050506'
const BORDER = 'rgba(255,255,255,0.08)'
const MUTED = 'rgba(226,232,240,0.75)'

/** One place for the small-caps treatment, so the floor cannot drift. */
const EYEBROW = 'text-[10px] font-semibold uppercase tracking-[0.12em]'

/** Black or white text on a tenant-coloured fill, whichever reads (the app picks the same way). */
function onColour(hex: string): string {
  const n = parseInt(hex.slice(1), 16)
  const lin = [n >> 16, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2] > 0.4 ? '#000' : '#fff'
}

function Eyebrow({ children }: { children: string }) {
  return (
    <span className="mb-2 flex items-center gap-2">
      <span className="h-px w-4 bg-white/[0.12]" aria-hidden="true" />
      <span className={EYEBROW} style={{ color: MUTED }}>
        {children}
      </span>
    </span>
  )
}

/** Studio V2's glass-card: panel over a faint sheen, 8px radius. */
const CARD = {
  background: `linear-gradient(180deg, rgba(255,255,255,0.028), rgba(255,255,255,0.008)), ${PANEL}`,
  borderColor: BORDER,
}

export default function BrandedPortalMockup() {
  const [tenant, setTenant] = useState<Tenant>(TENANTS[0])
  const buttonStyle = { background: tenant.accent, color: onColour(tenant.accent) }

  return (
    <div>
      <div
        role="group"
        aria-label="Preview the portal in a different brand"
        className="mb-5 flex flex-wrap items-center gap-2"
      >
        <span className="mr-1 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-white/40">
          Whose platform is it?
        </span>
        {TENANTS.map((option) => {
          const active = option.key === tenant.key
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={active}
              onClick={() => setTenant(option)}
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[0.8125rem] font-medium transition-all ${
                active
                  ? 'border-white/25 bg-white/[0.06] text-white'
                  : 'border-white/[0.08] text-white/45 hover:border-white/[0.18] hover:text-white/75'
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: option.accent }}
                aria-hidden="true"
              />
              {option.name}
            </button>
          )
        })}
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.1] shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
        {/* Browser chrome — the address bar is the fastest way to say "this is
            your domain, not a page inside ours". */}
        <div className="flex items-center gap-3 border-b border-white/[0.07] bg-white/[0.03] px-4 py-2.5">
          <div className="flex gap-1.5" aria-hidden="true">
            {['#ff5f57', '#febc2e', '#28c840'].map((dot) => (
              <span key={dot} className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
            ))}
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md bg-black/40 px-3 py-1">
            <Lock className="h-3 w-3 shrink-0 text-emerald-400" aria-hidden="true" />
            <span className="truncate text-[11px] text-white/55">{tenant.domain}</span>
          </div>
        </div>

        <div className="flex" style={{ background: SHELL }}>
          {/* ── Sidebar ─────────────────────────────────────────────────── */}
          <div
            className="hidden w-[168px] shrink-0 flex-col border-r sm:flex"
            style={{ background: PANEL, borderColor: BORDER }}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-3.5">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="flex shrink-0 items-end gap-[2px]" aria-hidden="true">
                  {[6, 11, 8, 4].map((h, i) => (
                    <span
                      key={i}
                      style={{ height: h, width: 2, background: tenant.accent, borderRadius: 1 }}
                    />
                  ))}
                </span>
                <span className="truncate text-[11px] font-bold tracking-[0.1em] text-white">
                  {tenant.wordmark}
                </span>
              </span>
              <ChevronsLeft className="h-3.5 w-3.5 shrink-0 text-white/25" aria-hidden="true" />
            </div>

            <nav aria-label="Primary" className="flex flex-col px-2 pb-3">
              {NAV.map((section) => (
                <div key={section.group ?? 'home'}>
                  {section.group && (
                    <>
                      <div
                        className="mx-1 my-2 border-t"
                        style={{ borderColor: BORDER }}
                        aria-hidden="true"
                      />
                      <p className={`mb-1 px-2 text-white/30 ${EYEBROW}`}>{section.group}</p>
                    </>
                  )}
                  <div className="flex flex-col gap-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon
                      const current = item.label === ACTIVE_NAV
                      return (
                        <span
                          key={item.label}
                          className="relative flex items-center gap-2 rounded-lg px-2.5 py-[5px] text-[11px]"
                          style={
                            current
                              ? {
                                  background: 'rgba(255,255,255,0.075)',
                                  color: '#fff',
                                  fontWeight: 600,
                                }
                              : { color: 'rgba(255,255,255,0.5)' }
                          }
                        >
                          {current && (
                            <span
                              className="absolute left-0 top-1/2 h-[58%] w-[2.5px] -translate-y-1/2 rounded-full"
                              style={{ background: tenant.accent }}
                              aria-hidden="true"
                            />
                          )}
                          <Icon
                            className="h-3.5 w-3.5 shrink-0"
                            style={current ? { color: tenant.accent } : undefined}
                            aria-hidden="true"
                          />
                          {item.label}
                        </span>
                      )
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* ── Content ─────────────────────────────────────────────────── */}
          <div className="min-w-0 flex-1">
            <div
              className="flex items-center justify-between gap-3 border-b px-4 py-3"
              style={{ borderColor: BORDER, background: PANEL }}
            >
              <p className="text-[13px] font-semibold text-white">Home</p>
              <span className="flex items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-[11px] font-semibold text-white/80"
                  aria-hidden="true"
                >
                  {tenant.user.initial}
                </span>
                <span className="hidden leading-tight md:block">
                  <span className="block text-[11px] font-medium text-white">
                    {tenant.user.name}
                  </span>
                  <span className="block text-[10px] text-white/40">{tenant.user.email}</span>
                </span>
                <ChevronsUpDown className="h-3 w-3 text-white/25" aria-hidden="true" />
              </span>
            </div>

            <div className="flex flex-col gap-2.5 p-3">
              <div className="grid gap-2.5 md:grid-cols-[1.7fr_1fr]">
                {/* Latest release, with its four-step journey. */}
                <div className="flex gap-3 rounded-lg border p-3.5" style={CARD}>
                  <div
                    className="flex aspect-square w-[72px] shrink-0 items-center justify-center self-start rounded-md border"
                    style={{
                      background: `linear-gradient(135deg, hsl(${TILES[0].hue} 55% 38%), hsl(${TILES[0].hue + 40} 60% 18%))`,
                      borderColor: BORDER,
                    }}
                    aria-hidden="true"
                  >
                    <Disc className="h-6 w-6 text-white/60" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Eyebrow>Latest release</Eyebrow>
                    <p
                      className="truncate text-[17px] font-semibold leading-tight text-[#f6f6fa]"
                      style={{ fontFamily: 'var(--wavbee-font-display)' }}
                    >
                      {TILES[0].title}
                    </p>
                    <p className="mt-0.5 text-[11px]" style={{ color: MUTED }}>
                      Single · Sample release
                    </p>
                    <ol className="m-0 mt-3 grid list-none grid-cols-4 gap-1.5 p-0">
                      {JOURNEY.map((step) => (
                        <li key={step.label} className="flex min-w-0 flex-col gap-1">
                          <span
                            className="h-1 rounded-full"
                            style={{ background: JOURNEY_BAR[step.state] }}
                            aria-hidden="true"
                          />
                          <span
                            className="flex items-center gap-0.5 text-[10px] leading-snug"
                            style={{
                              color: step.state === 'todo' ? MUTED : '#f6f6fa',
                              fontWeight: step.state === 'todo' ? 400 : 500,
                            }}
                          >
                            {step.state === 'done' && (
                              <Check
                                className="h-2.5 w-2.5 shrink-0"
                                style={{ color: 'rgb(34,197,94)' }}
                                aria-hidden="true"
                              />
                            )}
                            <span className="truncate">{step.label}</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                    <span
                      className="mt-3 inline-flex w-fit rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                      style={buttonStyle}
                    >
                      Continue editing
                    </span>
                  </div>
                </div>

                {/* Balance. */}
                <div className="flex flex-col rounded-lg border p-3.5" style={CARD}>
                  <Eyebrow>Balance</Eyebrow>
                  <p className="text-[10px]" style={{ color: MUTED }}>
                    Available to withdraw
                  </p>
                  <p
                    className="mt-1 text-[22px] font-bold tabular-nums leading-none text-[#f6f6fa]"
                    style={{ fontFamily: 'var(--wavbee-font-display)' }}
                  >
                    $312.40
                  </p>
                  <p className="mt-1 text-[10px] text-white/40">Sample figure</p>
                  <span
                    className="mt-auto inline-flex w-fit rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                    style={buttonStyle}
                  >
                    Withdraw
                  </span>
                </div>
              </div>

              {/* Your music. */}
              <div className="rounded-lg border p-3.5" style={CARD}>
                <div className="mb-3 flex items-end justify-between gap-3">
                  <div>
                    <Eyebrow>Catalog</Eyebrow>
                    <p
                      className="text-[15px] font-semibold text-[#f6f6fa]"
                      style={{ fontFamily: 'var(--wavbee-font-display)' }}
                    >
                      Your music
                    </p>
                  </div>
                  <span className="text-[10px]" style={{ color: MUTED }}>
                    Sample titles
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 lg:grid-cols-7">
                  <div
                    className="flex min-h-[88px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[10px] font-medium"
                    style={{ borderColor: 'rgba(255,255,255,0.2)', color: MUTED }}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    New release
                  </div>
                  {TILES.map((tile) => (
                    <div
                      key={tile.title}
                      className="flex min-w-0 flex-col gap-1.5 rounded-lg border p-1.5"
                      style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.32)' }}
                    >
                      <div
                        className="flex aspect-square w-full items-center justify-center rounded-md"
                        style={{
                          background: `linear-gradient(135deg, hsl(${tile.hue} 55% 38%), hsl(${tile.hue + 40} 60% 18%))`,
                        }}
                        aria-hidden="true"
                      >
                        <Disc className="h-1/3 w-1/3 text-white/50" />
                      </div>
                      <span className="min-w-0 px-0.5">
                        <span className="block truncate text-[11px] font-medium text-[#f6f6fa]">
                          {tile.title}
                        </span>
                        <span
                          className="flex items-center gap-1 text-[10px]"
                          style={{ color: MUTED }}
                        >
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ background: tile.dot }}
                            aria-hidden="true"
                          />
                          <span className="truncate">{tile.status}</span>
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mx-auto mt-4 max-w-2xl text-center text-[0.75rem] leading-relaxed text-white/45">
        This is one of <span className="text-white/70">your artists</span> signed in to
        <span className="text-white/70"> your</span> platform — their name, their releases and
        their balance (sample figures), on your domain and under your mark. Rebuilt from the
        current artist app, down to the 7.5% white the active nav item uses. Your colour appears
        where the product spends it: the logo, the active nav marker and the buttons.
      </p>
    </div>
  )
}
